import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useMemo, useState } from "react";
import { PageScaffold } from "../../shell/PageScaffold";
import { useVenue } from "../../shell/VenueContext";
import { EmbedPreview } from "./EmbedPreview";
import { useWidgetOrigin } from "./useWidgetOrigin";
import {
  EMBED_MODES,
  buildSnippet,
  buildStandaloneUrl,
  type EmbedLang,
  type EmbedMode,
} from "./snippet";

/**
 * `/v/:venueId/settings/embed` — the self-serve embed screen (tasks/booking-widget-embed.md). A
 * manager picks a display mode (floating recommended) and an optional default language, copies the
 * generated `<script>` snippet and the single standalone `book.<domain>/v/<slug>` link, and sees a
 * live preview that mounts the REAL loader. MANAGER/root-gated by the route (RequireVenueRole).
 *
 * Everything here is PURE CLIENT-SIDE composition: the snippet encodes only slug + mode (+ optional
 * lang); there is no embed-config table and no backend call (spec §"Snippet generation"). Branding,
 * hours and services are fetched live at widget load (R1), so a pasted snippet never needs replacing.
 * The widget origin comes from `useWidgetOrigin`.
 */
export function EmbedSettings() {
  const { venue } = useVenue();
  const { t } = useTranslation();
  const [mode, setMode] = useState<EmbedMode>("floating");
  const [lang, setLang] = useState<EmbedLang>("auto");
  const [copied, setCopied] = useState<"snippet" | "link" | null>(null);

  const origin = useWidgetOrigin();

  const snippet = useMemo(
    () => buildSnippet({ origin, slug: venue.slug, mode, lang }),
    [origin, venue.slug, mode, lang],
  );
  const standaloneUrl = useMemo(
    () => buildStandaloneUrl({ origin, slug: venue.slug, lang }),
    [origin, venue.slug, lang],
  );

  async function copy(value: string, which: "snippet" | "link") {
    try {
      await navigator.clipboard?.writeText(value);
      setCopied(which);
      window.setTimeout(() => setCopied((c) => (c === which ? null : c)), 2000);
    } catch {
      // Clipboard denied/unavailable: the value stays selectable in the field, so this is never a dead
      // end — nothing to surface.
    }
  }

  return (
    <PageScaffold title={`${t("embed.title")} — ${venue.displayName}`}>
      <div className={css({ display: "flex", flexDirection: "column", gap: "8", maxW: "3xl" })}>
        <p className={css({ fontSize: "sm", color: "fg.muted" })}>{t("embed.intro")}</p>

        <fieldset
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: "2",
            border: "none",
            p: "0",
          })}
        >
          <legend className={css({ fontSize: "sm", fontWeight: "semibold", mb: "1" })}>
            {t("embed.mode.legend")}
          </legend>
          {EMBED_MODES.map((m) => (
            <div
              key={m}
              className={css({
                display: "flex",
                gap: "2",
                alignItems: "flex-start",
                fontSize: "sm",
              })}
            >
              <input
                id={`embed-mode-${m}`}
                type="radio"
                name="embed-mode"
                value={m}
                checked={mode === m}
                onChange={() => setMode(m)}
                className={css({ mt: "1" })}
              />
              <label
                htmlFor={`embed-mode-${m}`}
                className={css({
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.5",
                  cursor: "pointer",
                })}
              >
                <span className={css({ fontWeight: "medium" })}>
                  {t(`embed.mode.${m}`)}
                  {m === "floating" ? (
                    <span
                      className={css({
                        ml: "2",
                        fontSize: "xs",
                        fontWeight: "medium",
                        color: "colorPalette.fg",
                        bg: "colorPalette.subtle",
                        px: "1.5",
                        py: "0.5",
                        rounded: "sm",
                      })}
                    >
                      {t("embed.mode.recommended")}
                    </span>
                  ) : null}
                </span>
                <span className={css({ color: "fg.muted" })}>{t(`embed.mode.${m}Hint`)}</span>
              </label>
            </div>
          ))}
        </fieldset>

        <label
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: "1",
            fontSize: "sm",
            maxW: "xs",
          })}
        >
          <span className={css({ fontWeight: "semibold" })}>{t("embed.lang.legend")}</span>
          <select
            value={lang}
            onChange={(e) => setLang(e.target.value as EmbedLang)}
            className={css({ borderWidth: "1px", rounded: "md", px: "2", py: "1.5" })}
          >
            <option value="auto">{t("embed.lang.auto")}</option>
            <option value="fr">{t("embed.lang.fr")}</option>
            <option value="en">{t("embed.lang.en")}</option>
          </select>
        </label>

        <section className={css({ display: "flex", flexDirection: "column", gap: "2" })}>
          <h3 className={css({ fontSize: "lg", fontWeight: "semibold" })}>
            {t("embed.snippet.title")}
          </h3>
          <p className={css({ fontSize: "sm", color: "fg.muted" })}>{t("embed.snippet.hint")}</p>
          <pre
            data-testid="embed-snippet"
            className={css({
              fontFamily: "mono",
              fontSize: "xs",
              whiteSpace: "pre-wrap",
              wordBreak: "break-all",
              p: "3",
              borderWidth: "1px",
              rounded: "md",
              bg: "bg.subtle",
            })}
          >
            <code>{snippet}</code>
          </pre>
          <div className={css({ display: "flex", gap: "3", alignItems: "center" })}>
            <button
              type="button"
              onClick={() => void copy(snippet, "snippet")}
              className={css({
                px: "3",
                py: "1.5",
                borderWidth: "1px",
                rounded: "md",
                fontSize: "sm",
                cursor: "pointer",
                bg: "colorPalette.default",
                color: "colorPalette.fg",
              })}
            >
              {t("embed.snippet.copy")}
            </button>
            <output aria-live="polite" className={css({ fontSize: "sm", color: "fg.success" })}>
              {copied === "snippet" ? t("embed.copied") : ""}
            </output>
          </div>
        </section>

        <section className={css({ display: "flex", flexDirection: "column", gap: "2" })}>
          <h3 className={css({ fontSize: "lg", fontWeight: "semibold" })}>
            {t("embed.standalone.title")}
          </h3>
          <p className={css({ fontSize: "sm", color: "fg.muted" })}>{t("embed.standalone.hint")}</p>
          <div
            className={css({ display: "flex", gap: "3", alignItems: "center", flexWrap: "wrap" })}
          >
            <a
              href={standaloneUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="embed-standalone-url"
              className={css({ fontSize: "sm", fontFamily: "mono", wordBreak: "break-all" })}
            >
              {standaloneUrl}
            </a>
          </div>
          <div className={css({ display: "flex", gap: "3", alignItems: "center" })}>
            <button
              type="button"
              onClick={() => void copy(standaloneUrl, "link")}
              className={css({
                px: "3",
                py: "1.5",
                borderWidth: "1px",
                rounded: "md",
                fontSize: "sm",
                cursor: "pointer",
              })}
            >
              {t("embed.standalone.copy")}
            </button>
            <output aria-live="polite" className={css({ fontSize: "sm", color: "fg.success" })}>
              {copied === "link" ? t("embed.copied") : ""}
            </output>
          </div>
        </section>

        <p role="note" className={css({ fontSize: "sm", color: "fg.muted" })}>
          {t("embed.live.note")}
        </p>

        <EmbedPreview origin={origin} slug={venue.slug} lang={lang} />
      </div>
    </PageScaffold>
  );
}
